import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, DatePicker, Form, Input, Select} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import dayjs from 'dayjs';
import {
  StyledInvoiceDetails,
  StyledInvoiceDetailsRadioGroup,
  StyledInvoiceDetailsVendorSpace,
  StyledInvoiceDetailsTable,
  StyledInvoiceDetailsTableSpace
} from './StyledInvoiceDetails';
import AmountGhostInput from '../../../AmountGhostInput';
import VendorSelectorWithAuth from '../../TransactionsPage/VendorSelectorWithAuth';
import {invoicesHelpers, InvoiceVatOptions, transactionsHelpers, vendorsHelpers} from '../../../../utils/helpers';
import {invoicePaymentStatusConstants, invoiceTypeConstants} from '../../../../constants';
import {vendorsActions} from '../../../../state/actions/vendors.actions';

const initialFormValues = {
  accrual_period: undefined,
  amount: '',
  date: undefined,
  due_date: undefined,
  paid_date: undefined,
  recipient: '',
  type: undefined,
  vat_type: undefined,
  vendor_local_id: '',
}

const {Item} = Form;

const formId = 'invoice-details-form';

const getDateValue = (value) => value ? value.format('YYYY-MM-DD') : undefined;
const formatDate = (date) => (date ? dayjs(date) : undefined);

const InvoiceDetails = ({
  invoice,
  isVendorEditDisabled = false,
  onEdit,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [vendor, setVendor] = useState(null);
  const [vatOptions,] = useState(InvoiceVatOptions());

  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const employees = useSelector(state => state.company.employees);

  const isAlreadyPaid = useMemo(() => invoice?.payment_status === invoicePaymentStatusConstants.PAID, [invoice]);

  const isInvoiceDocumentType = useMemo(() => invoice?.type === invoiceTypeConstants.INVOICE, [invoice]);

  const uploadedBy = useMemo(() => {
    const email = invoice?.uploaded_by;
    const employee = employees.find(employee => employee.email === email);
    return employee ? `${employee.full_name} <${email}>` : email || '';
  }, [employees, invoice]);

  const documentTypeOptions = useMemo(() => transactionsHelpers.getInvoiceTypeOptions({t}), [t]);

  const invalidVendor = useMemo(() => vendorsHelpers.isVendorToBeValidated(vendor), [vendor]);

  const loadVendorData = (invoice) => {
    const id = invoice?.vendor_local_id;
    if (!id) {
      if (vendor) setVendor(null);
      return;
    }

    dispatch(vendorsActions.getVendor({id, successCallback: setVendor}));
  }

  useEffect(() => {
    if (!invoice) return;

    form.setFieldsValue({
      amount: invoice?.amount || 0,
      accrual_period: formatDate(invoice?.accrual_period),
      date: formatDate(invoice?.date),
      due_date: formatDate(invoice?.due_date),
      invoice_number: invoice?.invoice_number || '',
      paid_date: formatDate(invoice?.paid_date),
      recipient: invoice?.recipient || '',
      type: invoice?.type || '',
      vat_type: invoice?.vat_type || '',
      vendor_local_id: invoice?.vendor_local_id,
    });
  }, [form, invoice]);

  useEffect(() => {
    loadVendorData(invoice);
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  const requiredRules = [{required: true, message: t('validation.fieldIsRequired')}];

  const handleSubmitForm = () => form.submit();

  const handlePressEnter = () => {
    handleSubmitForm();
    document.querySelectorAll(`#${formId} input`).forEach((input) => input.blur());
  }

  const handleEditVendorSuccess = () => {
    if (!invalidVendor) return;

    loadVendorData(invoice);
  }

  const formElementProps = {
    onBlur: handleSubmitForm,
    onPressEnter: handlePressEnter
  }

  const getDateField = (name, pickerProps) => (
    <Item
      name={name}
      rules={requiredRules}
    >
      <DatePicker
        format='DD MMM YYYY'
        style={{width: '100%'}}
        onChange={handleSubmitForm}
        {...pickerProps}
      />
    </Item>
  );

  const getSelectField = (name, options) => (
    <Item
      name={name}
      rules={requiredRules}
    >
      <Select
        format='DD MMM YYYY'
        style={{width: '100%'}}
        options={options}
        onChange={handleSubmitForm}
      />
    </Item>
  );

  const getInputField = (name) => (
    <Item name={name}>
      <Input {...formElementProps} />
    </Item>
  );

  const vendorField = (
    <StyledInvoiceDetailsVendorSpace
      align='center'
      size='small'
      wrap
    >
      {invalidVendor && <Alert message={`${t('add')} ${t('vat')}`} type='warning' showIcon />}
      <Item
        className='invoice-vendor-select'
        name='vendor_local_id'
      >
        <VendorSelectorWithAuth
          selectProps={{
            customSelectedRenderer: (props) =>
              vendorsHelpers.getVendorNameWithLink({
                vendor_id: props.value,
                name: props.name
              }),
            disabled: isVendorEditDisabled,
            isEnableAdd: false,
            isEnableAddVat: invalidVendor,
            showArrow: false,
          }}
          onChange={handleSubmitForm}
          onSubmitSuccess={handleEditVendorSuccess}
          reloadOnMissingValue={invalidVendor}
          showBeneficiaryInfo={false}
        />
      </Item>
    </StyledInvoiceDetailsVendorSpace>
  );

  const amountField = (
    <AmountGhostInput
      formItemProps={{rules: requiredRules}}
      inputProps={formElementProps}
      form={form}
      name='amount'
    />
  );

  const documentTypeField = (
    <Item name='type'>
      <StyledInvoiceDetailsRadioGroup
        onChange={handleSubmitForm}
        options={documentTypeOptions}
      />
    </Item>
  );

  const mainTableData = [
    isInvoiceDocumentType && {
      key: 'invoice_number',
      label: `${t('invoice')} ${t('number')}`,
      value: getInputField('invoice_number')
    },
    {
      key: 'issued_to',
      label: t('invoices:issuedTo'),
      value: getInputField('recipient')
    },
    {
      key: 'amount',
      label: t('amount'),
      value: amountField
    },
    {
      key: 'vat_type',
      label: t('vat'),
      value: getSelectField('vat_type', vatOptions)
    },
    {
      key: 'invoice-date',
      label: `${t('invoice')} ${t('date')}`,
      value: getDateField('date')
    },
    {
      key: 'due_date',
      label: `${t('due')} ${t('date')}`,
      value: getDateField('due_date')
    },
    isAlreadyPaid && {
      key: 'paid_date',
      label: `${t('payment')} ${t('date')}`,
      value: getDateField('paid_date', {disabled: true})
    },
    {
      key: 'accrual_period',
      label: t('accrualPeriod'),
      value: getDateField('accrual_period', {picker: 'month', format: 'MMM YYYY'})
    },
    {
      key: 'payment_status',
      label: t('status'),
      value: invoicesHelpers.getInvoiceStatusMark(invoice?.payment_status)
    },
    {
      key: 'uploaded_by',
      label: t('invoices:uploadedBy'),
      value: uploadedBy
    },
    {
      key: 'type',
      label: `${t('document')} ${t('type')}`,
      value: documentTypeField
    },
  ].filter(Boolean);

  const vendorTableData = [
    {
      key: 'vendor',
      label: t('vendor'),
      value: vendorField
    }
  ];

  const handleSubmit = (fields) => {
    let data = {
      accrual_period: getDateValue(fields.accrual_period),
      amount: fields.amount,
      date: getDateValue(fields.date),
      due_date: getDateValue(fields.due_date),
      recipient: fields.recipient || '',
      type: fields.type,
      vat_type: fields.vat_type,
    }
    if (!isVendorEditDisabled) {
      data = { ...data, vendor_local_id: fields.vendor_local_id };
    }
    if (isInvoiceDocumentType) {
      data = { ...data, invoice_number: fields.invoice_number || '' };
    }
    onEdit?.({data});
  }

  return (
    <StyledInvoiceDetails {...rest}>
      <Form
        id={formId}
        initialValues={initialFormValues}
        form={form}
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <StyledInvoiceDetailsTableSpace size='middle' direction='vertical'>
          <StyledInvoiceDetailsTable data={mainTableData} />
          {isInvoiceDocumentType && <StyledInvoiceDetailsTable data={vendorTableData} />}
        </StyledInvoiceDetailsTableSpace>
      </Form>
    </StyledInvoiceDetails>
  );
}

InvoiceDetails.propTypes = {
  invoice: PropTypes.shape({
    amount: PropTypes.number,
    date: PropTypes.string,
    due_date: PropTypes.string,
    payment_status: PropTypes.string,
    recipient: PropTypes.string,
    source_type: PropTypes.string,
    vendor: PropTypes.string,
    uploaded_by: PropTypes.string,
  }),
  isVendorEditDisabled: PropTypes.bool,
  onEdit: PropTypes.func,
}

export default InvoiceDetails;
